import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowUpRight, Check, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatBytes, formatCount } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { XmlDocument } from './convert';

/**
 * The IRIS XML, shown and not handed over.
 *
 * The public converter proves the file converts: the manifest, the issuer and the first
 * returns, written by the engine Tab Service files with. The complete transmission is what
 * Tab Service provides, so the page shows the opening of each one, lets the rest fade out,
 * and points at Tab Service for the whole file.
 *
 * Only the preview lines are put on the page. The conversion still runs in this tab, so the
 * full text is in the browser's memory; this keeps it off the screen and out of a download,
 * not out of reach of someone determined with developer tools.
 */

export const CONTACT_URL = 'https://www.tabservice.com/contact/';

/** Enough for the manifest, the issuer and the start of the first return. */
const PREVIEW_LINES = 44;
/** Lines drawn blurred under the fade, so the file visibly carries on. */
const FADE_LINES = 6;

export function XmlPreview({ documents, fileName }: { documents: XmlDocument[]; fileName: string }) {
  const [active, setActive] = useState(0);
  const section = useRef<HTMLElement>(null);
  const current = documents[Math.min(active, documents.length - 1)];

  // Built on request, further down the page than the button: bring it into view.
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    section.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }, [documents]);

  if (!current) return null;

  const lines = current.xml.replace(/\r\n/g, '\n').replace(/\n$/, '').split('\n');
  const shown = lines.slice(0, PREVIEW_LINES);
  const faded = lines.slice(PREVIEW_LINES, PREVIEW_LINES + FADE_LINES);
  const hidden = Math.max(0, lines.length - PREVIEW_LINES);
  const payees = documents.reduce((sum, document_) => sum + document_.payees, 0);
  const base = fileName.replace(/\.[^.]+$/, '');
  const gutter = String(lines.length).length;

  return (
    <section
      ref={section}
      aria-label="IRIS XML preview"
      className="scroll-mt-6 overflow-hidden rounded-xl border border-[color:var(--ts-border)] bg-surface shadow-[0_12px_32px_rgba(35,79,146,0.10)]"
    >
      {/* What was written, before the text itself. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-[color:var(--ts-border-soft)] bg-[color:var(--ts-blue-bglayout)] px-6 py-4">
        <Written>{formatCount(payees)} payees written</Written>
        <Written>
          {formatCount(documents.length)} transmission{documents.length === 1 ? '' : 's'}
        </Written>
        <Written>structure, data and mapping checked</Written>
      </div>

      {/* The document itself, drawn as the file it is. */}
      <div className="bg-[#0f1e38] text-[#e6edf7]">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-white/10 px-4 pt-3">
          <div role="tablist" aria-label="Transmissions" className="flex flex-wrap gap-1">
            {documents.map((document_, index) => {
              const selected = document_ === current;
              return (
                <button
                  key={`${document_.formType}-${document_.part}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setActive(index)}
                  className={cn(
                    'rounded-t-md border border-b-0 px-3 py-2 font-mono text-[13px] transition-colors',
                    'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#8fc1ff]',
                    selected
                      ? 'border-white/15 bg-[#16294a] text-white'
                      : 'border-transparent text-[#9fb2d0] hover:text-white',
                  )}
                >
                  {base}-{document_.formType}-{document_.part}.xml
                </button>
              );
            })}
          </div>
          <p className="pb-2 font-mono text-[12px] text-[#9fb2d0]">
            {current.formType}
            {current.parts > 1 ? ` · part ${current.part} of ${current.parts}` : ''} ·{' '}
            {formatCount(current.payees)} payees · {formatBytes(current.xml.length)} ·{' '}
            {formatCount(lines.length)} lines
          </p>
        </div>

        <div className="relative bg-[#16294a]">
          <div className="overflow-x-auto">
            <pre className="min-w-max py-4 font-mono text-[13px] leading-[1.6]">
              {shown.map((line, index) => (
                <CodeLine key={index} number={index + 1} width={gutter} line={line} />
              ))}
              {faded.length > 0 ? (
                <div aria-hidden="true" className="pointer-events-none blur-[2.5px] select-none">
                  {faded.map((line, index) => (
                    <CodeLine key={index} number={PREVIEW_LINES + index + 1} width={gutter} line={line} />
                  ))}
                </div>
              ) : null}
            </pre>
          </div>

          {hidden > 0 ? (
            <>
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-44 bg-gradient-to-b from-[#16294a]/0 via-[#16294a]/85 to-[#16294a]"
              />
              <div className="absolute inset-x-0 bottom-5 flex justify-center px-4">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#0f1e38]/90 px-4 py-1.5 font-mono text-[12px] text-[#c9d6ea] shadow-lg">
                  <Lock className="h-3.5 w-3.5 text-[#f3d79a]" aria-hidden="true" />
                  {formatCount(hidden)} more lines in the full transmission
                </span>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Where the rest comes from. */}
      <div className="flex flex-col gap-5 px-6 py-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="max-w-[60ch]">
          <h3 className="text-lg font-semibold text-gray-3">Your file converts. Tab Service can take it from here.</h3>
          <p className="mt-1.5 text-base leading-relaxed text-muted-foreground">
            This is the opening of each transmission. For the complete IRIS XML, or to have Tab Service
            transmit it to the IRS under its own IRIS transmitter code and keep the receipts, get in touch.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-3">
          <Button asChild variant="primary" size="lg">
            <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
              Contact Tab Service
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/sign-in">Sign in</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

function Written({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 text-base text-gray-3">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[color:var(--ts-pill-success-bg)] text-[color:var(--ts-pill-success-text)]">
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
      {children}
    </span>
  );
}

function CodeLine({ number, width, line }: { number: number; width: number; line: string }) {
  return (
    <div className="flex">
      <span
        aria-hidden="true"
        className="sticky left-0 shrink-0 bg-[#16294a] pr-4 pl-4 text-right text-[#5b6f93] select-none"
        style={{ width: `${width + 3}ch` }}
      >
        {number}
      </span>
      <span className="pr-6">
        {tokenize(line).map((token, index) => (
          <span key={index} className={TOKEN_CLASS[token.kind]}>
            {token.text}
          </span>
        ))}
      </span>
    </div>
  );
}

type TokenKind = 'decl' | 'punct' | 'tag' | 'attr' | 'value' | 'text' | 'space';

const TOKEN_CLASS: Record<TokenKind, string> = {
  decl: 'text-[#7d8fb0]',
  punct: 'text-[#6e86ad]',
  tag: 'text-[#8fc1ff]',
  attr: 'text-[#f3d79a]',
  value: 'text-[#a9e6c2]',
  text: 'text-[#f4f7fb]',
  space: '',
};

/**
 * One line of pretty-printed XML, cut into coloured pieces. The generators write one element
 * per line, so a line never needs what came before it to be read.
 */
export function tokenize(line: string): { kind: TokenKind; text: string }[] {
  const out: { kind: TokenKind; text: string }[] = [];
  const re = /(<\?[\s\S]*?\?>)|(<\/?)([\w:.-]+)|(\/?>)|([\w:.-]+)(=)("[^"]*")|(\s+)|([^<]+)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(line)) !== null) {
    if (match[1]) out.push({ kind: 'decl', text: match[1] });
    else if (match[2]) {
      out.push({ kind: 'punct', text: match[2] });
      out.push({ kind: 'tag', text: match[3] ?? '' });
    } else if (match[4]) out.push({ kind: 'punct', text: match[4] });
    else if (match[5]) {
      out.push({ kind: 'attr', text: match[5] });
      out.push({ kind: 'punct', text: '=' });
      out.push({ kind: 'value', text: match[7] ?? '' });
    } else if (match[8]) out.push({ kind: 'space', text: match[8] });
    else if (match[9]) out.push({ kind: 'text', text: match[9] });
  }
  return out;
}
